import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { LayoutGrid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { PageHeader } from '@/shared/layouts/PageHeader';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shared/utils/utils';
import { useLeads, useLeadMutations } from '../hooks/useLeads';
import { LeadsFilterBar } from '../components/LeadsFilterBar';
import { LeadsTable } from '../components/LeadsTable';
import { LeadsKanban } from '../components/LeadsKanban';
import { LeadDrawer } from '../components/LeadDrawer';
import { CallLogModal } from '../components/CallLogModal';
import { SubStatusModal } from '../components/SubStatusModal';
import { STATUS_LABELS } from '../constants';
import type { LeadsFilters } from '../types';

const PAGE_SIZE = 20;

export function LeadsPage() {
  const user = useUser();
  const isAdmin = ['admin', 'superadmin'].includes(user?.role || '');

  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [filters, setFilters] = useState<LeadsFilters>({ limit: PAGE_SIZE, offset: 0 });

  const { data, isLoading } = useLeads(filters);
  const { updateLead } = useLeadMutations();

  const leads = data?.leads || [];
  const total = data?.total || 0;
  const currentPage = Math.floor(filters.offset / PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [drawerLeadId, setDrawerLeadId] = useState<string | null>(null);
  const [callLogModal, setCallLogModal] = useState<{ isOpen: boolean; leadId: string | null; leadName: string }>({
    isOpen: false,
    leadId: null,
    leadName: '',
  });
  const [subStatusModal, setSubStatusModal] = useState<{ isOpen: boolean; leadId: string | null; mainStatus: string }>({
    isOpen: false,
    leadId: null,
    mainStatus: '',
  });

  const handleFilterChange = (newFilters: Partial<LeadsFilters>) =>
    setFilters((prev) => ({ ...prev, ...newFilters }));

  const handleResetFilters = () => setFilters({ limit: PAGE_SIZE, offset: 0 });

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.niche) params.set('niche', filters.niche);
    if (filters.location) params.set('location', filters.location);
    if (filters.status) params.set('status', filters.status);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.userId) params.set('userId', filters.userId);
    window.location.href = `/api/leads/export.csv?${params.toString()}`;
    toast.success('CSV download started');
  };

  const handleStatusChange = (id: string, newStatus: string) => {
    updateLead.mutate(
      { id, payload: { status: newStatus as never } },
      {
        onSuccess: () => {
          toast.success(`Status updated to ${STATUS_LABELS[newStatus] ?? newStatus}`);
          if (newStatus !== 'new') {
            setSubStatusModal({ isOpen: true, leadId: id, mainStatus: newStatus });
          }
        },
      },
    );
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Leads"
        description="Manage and track your sales pipeline."
        breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Leads' }]}
        actions={
          <div className="flex items-center gap-1 rounded-DEFAULT border border-outline bg-surface p-1">
            {([
              { mode: 'table' as const, icon: List, label: 'Table' },
              { mode: 'kanban' as const, icon: LayoutGrid, label: 'Board' },
            ]).map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => {
                  setViewMode(mode);
                  if (mode === 'kanban' && filters.limit !== 500) {
                    setFilters((prev) => ({ ...prev, limit: 500, offset: 0 }));
                  }
                  if (mode === 'table' && filters.limit !== PAGE_SIZE) {
                    setFilters((prev) => ({ ...prev, limit: PAGE_SIZE, offset: 0 }));
                  }
                }}
                className={cn(
                  'flex items-center gap-2 rounded-card-sm px-3 py-1.5 text-data-md font-medium transition-colors',
                  viewMode === mode
                    ? 'bg-primary-container text-on-primary-container'
                    : 'text-on-surface-variant hover:bg-surface-variant hover:text-on-surface',
                )}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        }
      />

      <LeadsFilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        onExport={handleExportCSV}
        isAdmin={isAdmin}
      />

      <div className="mt-4 flex flex-1 flex-col overflow-hidden rounded-card border border-outline bg-surface shadow-surface-1">
        {viewMode === 'table' ? (
          <div className="flex-1 overflow-y-auto">
            <LeadsTable
              leads={leads}
              isLoading={isLoading}
              isAdmin={isAdmin}
              onRowClick={(id) => setDrawerLeadId(id)}
              onStatusChange={handleStatusChange}
              onLogCall={(id, name) => setCallLogModal({ isOpen: true, leadId: id, leadName: name })}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4">
            <LeadsKanban
              leads={leads}
              isLoading={isLoading}
              onRowClick={(id) => setDrawerLeadId(id)}
              onStatusChange={handleStatusChange}
            />
          </div>
        )}

        {viewMode === 'table' && (
          <div className="flex items-center justify-between border-t border-outline bg-surface px-4 py-3">
            <div className="text-body-sm text-on-surface-variant">
              Page {currentPage + 1} of {totalPages} · {total} leads
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 0}
                onClick={() => setFilters((prev) => ({ ...prev, offset: prev.offset - PAGE_SIZE }))}
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages - 1}
                onClick={() => setFilters((prev) => ({ ...prev, offset: prev.offset + PAGE_SIZE }))}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <LeadDrawer leadId={drawerLeadId} onClose={() => setDrawerLeadId(null)} />

      <CallLogModal
        isOpen={callLogModal.isOpen}
        onClose={() => setCallLogModal({ isOpen: false, leadId: null, leadName: '' })}
        leadId={callLogModal.leadId}
        leadName={callLogModal.leadName}
      />

      <SubStatusModal
        isOpen={subStatusModal.isOpen}
        onClose={() => setSubStatusModal({ isOpen: false, leadId: null, mainStatus: '' })}
        leadId={subStatusModal.leadId}
        mainStatus={subStatusModal.mainStatus}
      />
    </div>
  );
}
